import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';

import { GenericForm, ConfirmationModal } from '@components';
import Button from '@components/Forms/Button';
import InputText from '@components/Inputs/InputText';
import Select from '@components/Forms/Select';
import { useBankAccount, useCommon } from '@hooks';
import { capitalize, substractOptionArray } from '@helpers';
import { formFieldsHaveValue } from '@helpers/validator';
import { defaultDocumentType } from '@constants/regions';
import classes from './FormBankAccount.module.scss';
import schema from './schema';

const FormBankAccount = ({ edit, ruts, accountTypes, bankNames }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setModal } = useCommon();
  const {
    bankAccount: initialBankAccount,
    createBankAccount,
    updateBankAccount,
  } = useBankAccount();
  const [bankAccount, setBankAccount] = useState((edit && initialBankAccount) || {});
  const [anyChange, setChange] = useState(false);

  const handleAcountNumberChange = (e) => {
    const { id, value } = e.target;
    setChange(true);
    setBankAccount((prevbankAccount) => ({
      ...prevbankAccount,
      owner_data: {
        ...prevbankAccount?.owner_data,
        [id]: value,
      },
    }));
  };

  const handleTypeChange = (e) => {
    setChange(true);
    setBankAccount({
      ...bankAccount,
      account_data: {
        ...accountTypes.find((item) => item.account_type_code === e.target.value),
        description: undefined,
      },
    });
  };

  const handleBankChange = (e) => {
    setChange(true);
    setBankAccount({
      ...bankAccount,
      bank_data: bankNames.find((item) => item.finantial_institution_id === e.target.value),
    });
  };

  const handleRutChange = (e) => {
    setChange(true);
    setBankAccount({
      ...bankAccount,
      owner_data: {
        ...bankAccount.owner_data,
        doc_number: e.target.value,
        doc_type: defaultDocumentType,
      },
    });
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleSubmit = () => {
    if (edit) {
      setModal({
        body: (
          <ConfirmationModal
            title={t('bankAccount.editBankAccount.modalConfirm.title')}
            handleConfirm={() => {
              updateBankAccount(bankAccount);
            }}
          />
        ),
      });
    } else {
      createBankAccount(bankAccount);
    }
  };

  const isSubmitDisabled = formFieldsHaveValue([
    bankAccount?.owner_data?.doc_number,
    bankAccount?.bank_data?.finantial_institution_id,
    bankAccount?.owner_data?.account_number,
    bankAccount?.account_data?.account_type_code,
  ]);

  return (
    <>
      {accountTypes.length > 0 && bankNames.length > 0 && (
        <Card className={['popup_card', classes.card].join(' ')}>
          {edit ? (
            <h3>{t('bankAccount.editBankAccount.subtitle')}</h3>
          ) : (
            <>
              <h3>{t('paymentsFoundsOut.common.bankAccount')}</h3>
              <h4>{t('paymentsFoundsOut.common.description')}</h4>
            </>
          )}
          <GenericForm
            data-testid="formbankaccount-form"
            justify="flex-end"
            schema={schema}
            item
            xs={12}
            onSubmit={handleSubmit}
            container
            spacing={3}
          >
            <Select
              fullWidth
              label={t('common.docType')}
              value={bankAccount?.owner_data?.doc_number}
              id="doc_number"
              placeholder={capitalize(defaultDocumentType)}
              className={classes.select}
              variant="outlined"
              handleMyChange={handleRutChange}
              options={ruts}
              gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
            />
            <Select
              fullWidth
              handleMyChange={handleBankChange}
              id="finantial_institution_name"
              label="Banco"
              value={bankAccount?.bank_data?.finantial_institution_id}
              variant="outlined"
              className={classes.select}
              options={substractOptionArray(
                bankNames,
                'finantial_institution_id',
                'finantial_institution_name'
              )}
              gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
            />
            <InputText
              label={t('paymentsFoundsOut.common.accountNumber')}
              handleChange={handleAcountNumberChange}
              value={bankAccount.owner_data?.account_number}
              id="account_number"
              className={classes.input}
              variant="outlined"
              gridProps={{ xs: 12, item: true, className: classes.gridInput }}
            />
            <Select
              fullWidth
              label={t('paymentsFoundsOut.common.accountType')}
              value={bankAccount?.account_data?.account_type_code}
              id="account_type"
              placeholder="Banco"
              className={classes.select}
              variant="outlined"
              handleMyChange={handleTypeChange}
              options={substractOptionArray(accountTypes, 'account_type_code', 'description')}
              gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
            />
            {edit ? (
              <Grid item xs={12} className={classes.formButtonContainer} gridProps={{ xs: 12 }}>
                <Button
                  id="cancel-edit-bank-account"
                  onClick={handleCancel}
                  variant="outlined"
                  data-testid="formbankaccount-cancel-button"
                >
                  {t('bankAccount.editBankAccount.cancel')}
                </Button>
                <Button
                  id="confirm-edit-bank-account"
                  type="submit"
                  data-testid="formbankaccount-save-button"
                  disabled={!anyChange || isSubmitDisabled}
                >
                  {t('bankAccount.editBankAccount.save')}
                </Button>
              </Grid>
            ) : (
              <Grid item xs={12} className={classes.formButtonContainer} gridProps={{ xs: 12 }}>
                <Button
                  onClick={handleCancel}
                  variant="outlined"
                  id="cancel-bank-account"
                  data-testid="formbankaccount-cancel-button"
                >
                  {t('bankAccount.editBankAccount.cancel')}
                </Button>
                <Button
                  cardButton
                  type="submit"
                  id="confirm-bank-account"
                  data-testid="formbankaccount-add-button"
                  disabled={isSubmitDisabled}
                >
                  {t('paymentsFoundsOut.common.add')}
                </Button>
              </Grid>
            )}
          </GenericForm>
        </Card>
      )}
    </>
  );
};

FormBankAccount.propTypes = {
  edit: PropTypes.bool,
  ruts: PropTypes.array.isRequired,
  accountTypes: PropTypes.array.isRequired,
  bankNames: PropTypes.array.isRequired,
};

FormBankAccount.defaultProps = {
  edit: false,
};

export default FormBankAccount;
